/**
 * Derives when the single-order "Mark as completed" action must stay disabled
 * for auth-on-file J-codes. Legacy AoF requires an uploaded letter whose
 * {@link JCodeDetails.auth_letter_review_status} is
 * {@link AuthLetterReviewStatus.ReviewedAndSaved}. A reviewed Status Tracking
 * approval uses its own review when no legacy AoF document/review exists.
 *
 * @module authLetterReviewMarkCompleteGuard
 */
import {
  FaxLetterCategory,
  FaxLetterReviewStatus,
  OrderFaxLetter,
} from "../../../api/postCall/faxLetter";
import type {
  AuthOnFile,
  JCodeDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { AuthLetterReviewStatus } from "../../../enums/authLetterReviewStatus";
import { DocumentTypesForOrder } from "../../../enums/documentTypes";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { checkAuthLetterUploadStatus } from "../../worklists/singleOrder/cptCodes/utils/checkAuthLetterUploadStatus";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";

/**
 * Whether this J-code represents auth-on-file with a letter available from
 * either CPT `documents` (auth_letter type) or nested `auth_letter.auth_letter_path`.
 *
 * @param jCode - J-code row from auth-on-file entries
 * @returns True when status is auth-on-file and a non-empty letter path exists
 */
const hasAuthOnFileLetterUploaded = (jCode: JCodeDetails): boolean => {
  if (jCode.auth_status !== MedicalPaOrdersAuthStatus.AuthOnFile) {
    return false;
  }
  if (checkAuthLetterUploadStatus(jCode.auth_status, jCode.documents)) {
    return true;
  }
  return Boolean(jCode.auth_letter?.auth_letter_path?.trim());
};

/** A legacy AoF document or review must continue through the existing AoF validation. */
export const hasLegacyAuthLetterEvidence = (jCode: JCodeDetails): boolean =>
  Boolean(
    jCode.auth_letter_review_status?.trim() ||
    jCode.auth_letter?.auth_letter_path?.trim() ||
    jCode.documents?.some(
      (document) => document?.doc_type === DocumentTypesForOrder.AUTH_LETTER,
    ),
  );

/**
 * Recognizes the saved Status Tracking approval after a manual change to AoF.
 * The API's newest attached letter and its effective reviewed category are authoritative;
 * a PDF upload alone, an older review, or a review for another drug is insufficient.
 */
const hasReviewedFaxApproval = (
  jCode: JCodeDetails,
  faxLettersByJCodeId?: ReadonlyMap<string, OrderFaxLetter>,
): boolean => {
  if (!jCode.id || hasLegacyAuthLetterEvidence(jCode)) return false;
  const letter = faxLettersByJCodeId?.get(jCode.id);
  return Boolean(
    letter?.j_code_id === jCode.id &&
    letter.document_id?.trim() &&
    letter.review_status === FaxLetterReviewStatus.Reviewed &&
    letter.category === FaxLetterCategory.Approval,
  );
};

/**
 * Returns true when any J-code with {@link MedicalPaOrdersAuthStatus.AuthOnFile}
 * is missing an uploaded letter (per CPT `documents` or `auth_letter.auth_letter_path`),
 * or has a letter whose `auth_letter_review_status` is not yet
 * {@link AuthLetterReviewStatus.ReviewedAndSaved} (empty or `NewLetterUploaded`).
 * A saved Status Tracking approval satisfies review only when the row has no legacy
 * AoF evidence. Other auth statuses are ignored.
 *
 * @param authOnFile - Auth-on-file subtree from order detail (`entries` / `auth_entries`)
 * @param faxLettersByJCodeId - Newest attached fax per J-code, after successful loading
 * @returns Whether the mark-as-completed primary action should be disabled
 */
export const shouldDisableMarkCompleteForAuthLetterReviewStatus = (
  authOnFile: AuthOnFile | null | undefined,
  faxLettersByJCodeId?: ReadonlyMap<string, OrderFaxLetter>,
): boolean => {
  const jCodes = getAllJCodesFromAuthOnFile(authOnFile ?? undefined);
  if (!jCodes?.length) {
    return false;
  }
  return jCodes.some((jCode) => {
    if (jCode.auth_status !== MedicalPaOrdersAuthStatus.AuthOnFile) {
      return false;
    }
    if (hasReviewedFaxApproval(jCode, faxLettersByJCodeId)) {
      return false;
    }
    if (!hasAuthOnFileLetterUploaded(jCode)) {
      return true;
    }
    const status = (jCode.auth_letter_review_status ?? "").trim();
    return status !== AuthLetterReviewStatus.ReviewedAndSaved;
  });
};
